'use client';

import clsx from 'clsx';
import { FC, PropsWithChildren, useState } from 'react';
import { Button } from 'atoms/Button';
import { motion } from 'modules/framer-motion';
import { useTranslations } from 'modules/i18n/useTranslations';
import { IconSVG } from '../../atoms/IconSVG';
import styles from './styles.module.css';

type CollapseNodeProps = PropsWithChildren<{
  colapseLineCount?: number;
}>;

export const CollapseNode: FC<CollapseNodeProps> = ({
  children,
  colapseLineCount = 2,
}) => {
  const t = useTranslations();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const calculateCollapsedHeight = (ref: HTMLDivElement) => {
    if (!ref) {
      return;
    }

    setCollapsedHeight(ref.clientHeight);
  };

  const toggleCollapse = () => {
    setIsAnimating(true);
    setIsCollapsed((prev) => !prev);
  };

  return (
    <div className={styles.container}>
      {/* Reference element to calculate collapsed height */}
      <div
        data-testid="collapse-node-ref-content"
        ref={calculateCollapsedHeight}
        className={clsx(styles.contentRef, styles.wrapper, styles.collapsed)}
        style={{
          WebkitLineClamp: colapseLineCount,
        }}
      >
        {children}
      </div>

      <motion.div
        data-testid="collapse-node-content"
        className={clsx(styles.wrapper, isCollapsed && styles.collapsed)}
        style={{
          WebkitLineClamp:
            !isAnimating && isCollapsed ? colapseLineCount : undefined,
        }}
        initial={false}
        animate={{
          height: isCollapsed
            ? collapsedHeight === 0
              ? 'auto'
              : collapsedHeight
            : 'auto',
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
          onComplete: () => {
            setIsAnimating(false);
          },
        }}
      >
        {children}
      </motion.div>

      <div>
        <Button
          onClick={toggleCollapse}
          className={styles.button}
          variant="text"
          disableRipple
          endIcon={
            <IconSVG
              type={isCollapsed ? 'plus' : 'minus'}
              className={styles.icon}
            />
          }
        >
          {isCollapsed ? (
            <span>{t('common.buttons.read-more')}</span>
          ) : (
            <span>{t('common.buttons.read-less')}</span>
          )}
        </Button>
      </div>
    </div>
  );
};
